import { Bell, Mail, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@lytra/frontend-common/components/v2/ui/badge";
import { Chip } from "@lytra/frontend-common/components/v2/ui/chip";
import type { IconComponent } from "@lytra/frontend-common/components/v2/ui/icon";
import { Throbber } from "@lytra/frontend-common/components/v2/ui/throbber";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@lytra/frontend-common/components/v2/ui/tooltip";
import { cn } from "@lytra/frontend-common/components/v2/lib/utils";
import type { RecipientChannel } from "./recipients";

export interface RecipientChipProps {
  /** What the chip names: the person's full name for a Lytra match, else the address. */
  label: string;
  channel: RecipientChannel;
  /** A "per Mail" address outside the tenant's domains (D-10). Only meaningful with `channel="mail"`. */
  foreign?: boolean;
  onRemove?: () => void;
  /** A brief highlight: the agent tried to add this recipient again. */
  pulse?: boolean;
}

// The v2 Chip draws its leading icon in `--ink-muted` at 12px; these two keep that size and
// stroke (they get them from `Icon`) and only change what the cue means. Module level, so the
// component identity stays stable across renders.
const WarningIcon: IconComponent = ({ className, ...props }) => (
  <TriangleAlert {...props} className={cn(className, "text-warning-strong")} />
);
const CheckingIcon: IconComponent = () => <Throbber size={12} decorative />;

/**
 * One recipient of "An Kollegen senden" (UI-SPEC section 3), on the v2 `Chip` frame so remove
 * and Delete / Backspace stay the Chip's own. The channel is told by icon and words first:
 * in Lytra (avatar, info badge with bell), per Mail (mail icon, neutral badge), and the
 * foreign-domain warning (triangle, `--warning-strong` border on `--warning-subtle`, tooltip),
 * never by the tint alone (D-10). Staff Orange is not used. Everything a person typed is
 * rendered as React text (T-03-10).
 */
export function RecipientChip({
  label,
  channel,
  foreign = false,
  onRemove,
  pulse = false,
}: RecipientChipProps) {
  const { t } = useTranslation();
  const warning = channel === "mail" && foreign;

  let accessibleName = label;
  if (channel === "lytra") {
    accessibleName = t("tickets.forward.recipients.chipLytra", { label });
  } else if (channel === "mail") {
    accessibleName = t(
      warning
        ? "tickets.forward.recipients.chipForeign"
        : "tickets.forward.recipients.chipMail",
      { label },
    );
  } else if (channel === "checking") {
    accessibleName = `${label}, ${t("tickets.forward.recipients.checking")}`;
  }

  const badge =
    channel === "lytra" ? (
      <Badge tone="info" size="xs" icon={Bell} aria-hidden="true">
        {t("tickets.forward.recipients.channelLytra")}
      </Badge>
    ) : channel === "mail" ? (
      <Badge tone="neutral" size="xs" aria-hidden="true">
        {t("tickets.forward.recipients.channelMail")}
      </Badge>
    ) : null;

  const chip = (
    <Chip
      role="group"
      aria-label={accessibleName}
      label={label}
      avatar={channel === "lytra" ? { name: label } : undefined}
      icon={
        channel === "lytra"
          ? undefined
          : warning
            ? WarningIcon
            : channel === "checking"
              ? CheckingIcon
              : Mail
      }
      trailing={badge}
      onRemove={onRemove}
      removeLabel={t("tickets.forward.recipients.remove", { label })}
      className={cn(
        "motion-safe:transition-colors motion-reduce:transition-none",
        warning && "border-warning-strong bg-warning-subtle",
        // Both fills would fight (the later class wins): a foreign chip keeps its warning tint
        // and shows the pulse as a ring in the warning colour instead.
        pulse && !warning && "motion-safe:bg-hover-overlay",
        pulse && warning && "motion-safe:ring-2 motion-safe:ring-warning-strong",
      )}
    />
  );

  const hint = warning
    ? t("tickets.forward.warning.foreign")
    : channel === "checking"
      ? t("tickets.forward.recipients.checking")
      : null;
  if (hint === null) return chip;

  // The Chip's own tooltip shows a cut label; this one carries the warning (or the
  // "being checked" mark) whenever the chip is hovered or focused.
  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}
